import BaseApiModule from 'src/api/base';
import { ICartItem } from '../cart';

interface VoucherValidationRequest {
  code: string;
  salePointId: number;
  cartItems: ICartItem[];
  cartTotal: number;
}

interface FreeProductInfo {
  id: number;
  name: string;
  price: number;
  imageUrl?: string;
  ektacoCode?: string;
}

export interface VoucherRecommendation {
  voucherCode: string;
  freeProduct: FreeProductInfo;
  freeQuantity: number;
  savingsAmount: number;
}

interface VoucherRecommendationsResult {
  recommendations: VoucherRecommendation[];
}

interface VoucherValidationResult {
  isValid: boolean;
  voucher?: {
    id: number;
    code: string;
    type:
      | 'fixed_discount'
      | 'percentage_product'
      | 'percentage_cart'
      | 'free_product'
      | 'conditional_free'
      | 'gift_card';
    discountValue?: number;
    giftCardAmount?: number;
    name?: string;
    description?: string;
  };
  discountAmount: number;
  freeProducts?: FreeProductInfo[];
  error?: string;
  errorCode?: string;
}

class VoucherApi extends BaseApiModule {
  protected baseRoute = '/vouchers';

  /**
   * Validate voucher code against current cart and sale point
   */
  public async validateVoucher(
    code: string,
    salePointId: number,
    cartItems: ICartItem[],
    cartTotal: number
  ): Promise<VoucherValidationResult> {
    // customer identity is derived server-side from the JWT auth header;
    // the API rejects customerId/customerPhone in the body
    const request: VoucherValidationRequest = {
      code,
      salePointId,
      cartItems,
      cartTotal,
    };

    try {
      const result = await this._post<VoucherValidationResult>(`${this.baseRoute}/validate`, request);
      return result;
    } catch (error) {
      console.error('Voucher validation failed:', error);
      return {
        isValid: false,
        discountAmount: 0,
        error: error.response?.data?.error || 'Voucher validation failed',
        errorCode: error.response?.data?.errorCode || 'VALIDATION_ERROR',
      };
    }
  }

  /**
   * Check eligible voucher recommendations for current cart
   */
  public async checkRecommendations(
    salePointId: number,
    cartItems: ICartItem[]
  ): Promise<VoucherRecommendationsResult> {
    try {
      return await this._post<VoucherRecommendationsResult>(`${this.baseRoute}/check-recommendations`, {
        salePointId,
        cartItems,
      });
    } catch (error) {
      console.error('Voucher recommendations check failed:', error);
      return { recommendations: [] };
    }
  }
}

export default VoucherApi;
export type { VoucherValidationResult, VoucherValidationRequest, FreeProductInfo };
